import { Injectable } from '@angular/core';

import { Storage } from '../../class/workflow.type';
import { ConfigService } from '../config/config.service';

import { PATH_ITERATION_SYMBOL, PATH_PAGE_ITERATION_SYMBOL } from '../../class/stateful-value.class';

interface ConfigExtract {
  protocol: string;
  config: Storage;
  id: string;
  iterationId?: number;
  pageIterationId?: number;
  storageId: string;
}

const NUMBER_ONLY_RX = /[0-9]+/;

/** Service to handle storage backends for any StatefulValue */
@Injectable({
  providedIn: 'root'
})
export class StorageInfoService {
  constructor(private readonly configService: ConfigService) {}

  /**
   * Gets config entry for storageId even if it has an iteration ID incorporated instead of the $
   * If the storage id has iteration index, it will return storage id with $ symbol
   * for example for a storage id api:loanApplications/0/primaryConsumerId the result will be
   * {
   *  protocol: "api"
   *  id: loanApplications/0/primaryConsumerId
   *  storageId: api:loanApplications/$/primaryConsumerId
   *  iterationId: 0
   * }
   * @param storageId id from storage-*.json with protocol (or its version with iterationId) (ex: "mem:a/b")
   */
  public parse(storageId: string): ConfigExtract {
    const [protocol, path] = storageId.split(':');
    const storageConfig = this.configService.config.storage[protocol];

    if (!storageConfig) {
      throw new Error(
        `storage config '${protocol}' not found. Make sure storage.${protocol}.json has been created and added into .groups.json.`
      );
    }

    let config = storageConfig[path];
    if (config) {
      return { config, id: path, storageId, protocol };
    }
    const idParts: number[] = [];
    const parts = path.split('/');
    const partsLen = parts.length;
    for (let i = 0; i < partsLen; i++) {
      if (parts[i].match(NUMBER_ONLY_RX)) {
        idParts.push(i);
      }
    }
    const idPartsLen = idParts.length;
    for (let i = 0; i < idPartsLen; i++) {
      let testPath = this.configTestPath(parts, idParts, i);
      config = storageConfig[testPath];
      if (config) {
        return { config, id: testPath, pageIterationId: +parts[idParts[i]], storageId: `${protocol}:${testPath}`, protocol };
      }
      testPath = this.configTestPath(parts, idParts, null, i);
      config = storageConfig[testPath];
      if (config) {
        return { config, id: testPath, iterationId: +parts[idParts[i]], storageId: `${protocol}:${testPath}`, protocol };
      }
      for (let ii = i + 1; ii < idPartsLen; ii++) {
        testPath = this.configTestPath(parts, idParts, i, ii);
        config = storageConfig[testPath];
        if (config) {
          const stId = `${protocol}:${testPath}`;
          return { config, id: testPath, iterationId: +parts[idParts[ii]], pageIterationId: +parts[idParts[i]], storageId: stId, protocol };
        }
      }
    }
  }

  private configTestPath(parts: string[], idParts: number[], pageIterationIdx?: number, iterationIdx?: number): string {
    const testParts = [...parts];
    if (pageIterationIdx != null) {
      testParts[idParts[pageIterationIdx]] = PATH_PAGE_ITERATION_SYMBOL;
    }
    if (iterationIdx != null) {
      testParts[idParts[iterationIdx]] = PATH_ITERATION_SYMBOL;
    }
    return testParts.join('/');
  }
}
